import React from 'react'
import { renderHeadshot } from '../../../pages/Reports/helpers/renderHeadshot.function'
import { getHeadshot } from '../../../shared/utilities/getHeadshot.function'
import { formatRelativeTime } from './relativeTime'

// Mirrors the STATUS map in LiveLeaderboardSidebar.component.jsx. Defined
// locally so the mobile bar can render its own pre-row placeholders without
// importing back from the parent (keeps the dep direction one-way:
// MobileBar ← Sidebar).
const STATUS = {
  LOADING: 'loading',
  ERROR: 'error',
  EMPTY: 'empty',
  READY: 'ready',
}

/**
 * `LiveLeaderboardMobileBar` — pure presentation component for the
 * `<768px` variant of the LL feature. Rendered inside
 * `SidebarSlider`'s `__mobile-slot` (which owns position, breakpoint
 * show/hide, and z-index). Shows the most recent sale only — no list, no
 * toggle, no scroll. Pre-row placeholders fill the reserved space during
 * hydrate or network errors so the bar isn't blank.
 *
 * @param {Object} props
 * @param {Object|null} props.row - Most recent sale row (live feed head),
 *   or `null` before hydrate / on empty feed / on error.
 * @param {number} props.nowTick - Epoch ms tick used by `formatRelativeTime`
 *   to keep the "5m ago" label fresh. Updated every 30s by the parent.
 * @param {'connecting'|'connected'|'degraded'} props.connection - WebSocket
 *   connection state for the pulse dot indicator.
 * @param {boolean} props.isNew - Whether the current `row` is freshly
 *   arrived (within `NEW_ROW_HIGHLIGHT_MS` of broadcast). Applies the
 *   `is-new` class which triggers the `llb-row-flash` animation.
 * @param {'loading'|'error'|'empty'|'ready'} props.status - Hydrate state
 *   from the parent. When `row` is null, drives the placeholder label.
 */
function LiveLeaderboardMobileBar({ row, nowTick, connection, isNew, status }) {
  // Pre-row placeholders so the reserved space isn't blank during hydrate /
  // network errors. These are short, single-line, non-interactive.
  if (!row) {
    let label = 'Live feed'
    if (status === STATUS.LOADING) label = 'Live feed · loading…'
    else if (status === STATUS.ERROR) label = 'Live feed unavailable'
    else if (status === STATUS.EMPTY) label = 'Live feed · no sales yet'
    return (
      <div className='LiveLeaderboardMobile LiveLeaderboardMobile--placeholder' role='status' aria-live='polite'>
        <span
          className={`LiveLeaderboardMobile__pulse LiveLeaderboardMobile__pulse--${connection}`}
          aria-hidden='true'
        />
        <span className='LiveLeaderboardMobile__placeholder-label'>{label}</span>
      </div>
    )
  }

  const { agent, carrier, coverage, av_points, av_created } = row
  const agentPic = getHeadshot({ id: agent.id, u_picture: agent.u_picture })

  return (
    <div
      className={`LiveLeaderboardMobile${isNew ? ' is-new' : ''}`}
      role='status'
      aria-live='polite'
      aria-label='Most recent sale'
    >
      <span className={`LiveLeaderboardMobile__pulse LiveLeaderboardMobile__pulse--${connection}`} aria-hidden='true' />
      <div className='LiveLeaderboardMobile__avatar'>
        {renderHeadshot({
          agentPic,
          agentName: agent.name,
          agentId: agent.id,
          isConfQualified: agent.conf_qualifier,
        })}
      </div>
      <div className='LiveLeaderboardMobile__body'>
        <div className='LiveLeaderboardMobile__name'>{agent.name}</div>
        <div className='LiveLeaderboardMobile__meta'>
          {carrier} · {coverage}
        </div>
      </div>
      <div className='LiveLeaderboardMobile__trail'>
        <span className='LiveLeaderboardMobile__points'>{av_points.toLocaleString()} pts</span>
        <span className='LiveLeaderboardMobile__time'>{formatRelativeTime(av_created, nowTick)}</span>
      </div>
    </div>
  )
}

export default LiveLeaderboardMobileBar
